import React, { Component, type ComponentType, type ReactNode, type ErrorInfo } from 'react';

import { useInteractionContext, type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import {
	withAnalyticsEvents,
	type UIAnalyticsEvent,
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import fireErrorAnalytics from './fire-error-analytics.tsx';

interface Props extends WithAnalyticsEventsProps {
	// We are overloading `attributes` with a callback. For backwards compatibility we don't want to change the prop name

	attributes?: AnalyticsAttributes | ((arg1: Error) => AnalyticsAttributes);
	children: ReactNode;
	// this prop is coming from Atlaskit, can't rename it
	createAnalyticsEvent: NonNullable<WithAnalyticsEventsProps['createAnalyticsEvent']>;
	id: string;
	packageName?: string;
	teamName?: string;
	sendToPrivacyUnsafeSplunk?: boolean;
	ufoLabelStack?: LabelStack;
}

type State = {
	analyticsEvent: UIAnalyticsEvent | null;
};

const getView = (labelStack?: LabelStack | null) => {
	if (!labelStack || labelStack.length < 3) {
		return undefined;
	}

	// for Jira, first UFO label is jira-spa, second is route-component, third is the view
	return labelStack[2].name;
};

class CatchAndReportErrors extends Component<Props, State> {
	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const {
			packageName,
			teamName,
			id,
			attributes,
			createAnalyticsEvent,
			sendToPrivacyUnsafeSplunk,
			ufoLabelStack,
		} = this.props;
		const analyticsEvent = createAnalyticsEvent({ action: 'failed' });

		fireErrorAnalytics({
			event: analyticsEvent,
			errorInfo,
			error,
			meta: {
				id,
				packageName,
				teamName,
			},
			attributes: typeof attributes === 'function' ? attributes(error) : attributes,
			sendToPrivacyUnsafeSplunk,
			view: fg('jira-report-unhandled-errors-by-view') ? getView(ufoLabelStack) : undefined,
		});

		throw error;
	}

	render() {
		return this.props.children;
	}
}

type ReportErrorsType = ComponentType<
	Flow.Diff<
		Props,
		{
			createAnalyticsEvent: Props['createAnalyticsEvent'];
		}
	>
>;

const ReportErrorsWithAnalytics: ReportErrorsType = withAnalyticsEvents()(CatchAndReportErrors);

const ReportErrorsWithUFOContext: ReportErrorsType = (props) => {
	const interactionContext = useInteractionContext();
	return <ReportErrorsWithAnalytics {...props} ufoLabelStack={interactionContext?.labelStack} />;
};

export default componentWithFG(
	'jira-report-unhandled-errors-by-view',
	ReportErrorsWithUFOContext,
	ReportErrorsWithAnalytics,
);
